import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Form, Input, Select, DatePicker, InputNumber, message } from 'antd';
import { useNavigate } from 'react-router';
import { ENavigationPath } from 'constants/path';
import {
  CreateUploadedFilePayloadDtoEntityTypeEnum,
  BinderResponseDtoSecurityEnum,
  BinderResponseDtoBinderGroupEnum,
  BinderResponseDtoTerritoriesEnum,
  BinderResponseDtoLineOfBusinessesEnum,
  BinderResponseDtoPlaceBrokerEnum,
  CreateBinderPayloadDto,
  ReplaceBinderPayloadDto,
  BinderResponseDtoStatusEnum,
  UploadedFileResponseDto,
} from 'libs/pinnacle-sdk';
import { ConfirmationModal } from 'components/ui/confirmation-modal';
import { isEqual, uniq, uniqBy, difference, isEmpty } from 'lodash';
import { handleError, sanitizeFormValue, validateForm } from 'utils/form';
import { fields, getValidationSchema, initialValues } from './form';
import { ConfirmationModalType } from 'components/ui/confirmation-modal/types';
import FormContainer from 'components/ui/form-container';
import FormSection from 'components/ui/form-section';
import FormLayoutGroup from 'components/ui/form-layout-group';
import FormActionButtons from 'components/ui/form-action-buttons';
import { ArrowDownIcon } from 'assets/icons/arrow-down';
import FileUploader from 'components/features/file-uploader';
import { useTranslation } from 'react-i18next';
import CalendarIcon from 'assets/icons/calendar';
import { EFileStatus } from '../file-uploader/enum';
import { referenceDataApi } from 'services/apis';
import { EQueryKeys } from 'constants/query-keys';
import { useQuery } from 'react-query';
import TextArea from 'antd/es/input/TextArea';
import { formatLocaleNumber } from 'utils/format';
import { useFileSession } from 'hooks/useFileSession';
import StatusIndicator from 'components/ui/status-indicator';
import { getUnderwritingYearsOptions } from 'utils';
import { SortedSelect } from 'components/ui/sorted-select';
import { useWatch } from 'antd/es/form/Form';

interface BinderFormProps {
  mode: 'create' | 'update';
  initialData?: object;
  files?: UploadedFileResponseDto[];
  onSubmit: (data: {
    binder: CreateBinderPayloadDto | ReplaceBinderPayloadDto;
    fileIds: string[];
  }) => Promise<void>;
  isLoading?: boolean;
}

export const BinderForm: React.FC<BinderFormProps> = ({
  mode,
  initialData,
  onSubmit,
  isLoading = false,
  files,
}) => {
  const [isFormValid, setIsFormValid] = useState(false);
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [showConfirmation, setShowConfirmation] = useState(false);
  const { t } = useTranslation();
  const hasFormBeenUpdated = useRef(false);
  const [manuallyDeselectedMarkets, setManuallyDeselectedMarkets] = useState<
    string[]
  >([]);

  const resetAndNavigate = useCallback(() => {
    form.resetFields();
    setShowConfirmation(false);
    navigate(ENavigationPath.BINDER_RECORDS);
  }, [form, navigate]);

  const { fileSessionId, fileList, setFileList } = useFileSession(files);
  const territories = useWatch('territories', form);
  const markets = useWatch('markets', form);

  const { data: currencies, isLoading: isLoadingCurrencies } = useQuery(
    EQueryKeys.CURRENCIES,
    async () => {
      return referenceDataApi.getCurrencies();
    },
    {
      refetchOnWindowFocus: false,
      staleTime: Infinity,
    }
  );

  const {
    data: excludedPrincipalOccupations,
    isLoading: isLoadingExcludedPrincipalOccupations,
  } = useQuery(
    EQueryKeys.PRINCIPAL_OCCUPATIONS,
    async () => {
      return referenceDataApi.getPrincipalOccupations();
    },
    {
      refetchOnWindowFocus: false,
      staleTime: Infinity,
    }
  );

  const {
    data: mainRegionAndRiskTerritoryList,
    isLoading: isLoadingMainRegionAndRiskTerritoryList,
  } = useQuery(
    [EQueryKeys.MAIN_REGION_AND_RISK_TERRITORY_LIST, territories],
    async () => {
      return referenceDataApi.getMainRegionAndRiskTerritoryList({
        getMainRegionAndRiskTerritoryListQueryDto: { regions: territories },
      });
    },
    {
      refetchOnWindowFocus: false,
      staleTime: Infinity,
    }
  );

  useEffect(() => {
    (async () => {
      if (hasFormBeenUpdated.current) return;

      if (initialData) {
        form.setFieldsValue(initialData);
        const isValid = await validateForm(
          initialData,
          form,
          fields,
          getValidationSchema(t)
        );
        setIsFormValid(isValid);
        hasFormBeenUpdated.current = true;
      } else if (!isLoading) {
        hasFormBeenUpdated.current = true;
      }
    })();
  }, [form, initialData, t]);

  useEffect(() => {
    if (!hasFormBeenUpdated.current) return;
    if (!territories || territories.length === 0) {
      form.setFieldsValue({ markets: [] });
    }
  }, [territories]);

  useEffect(() => {
    if (!hasFormBeenUpdated.current || isLoadingMainRegionAndRiskTerritoryList)
      return;

    const validMarkets = markets?.filter((market: string) =>
      mainRegionAndRiskTerritoryList?.some(
        (mainRegionAndRiskTerritory) =>
          mainRegionAndRiskTerritory.country === market
      )
    );

    const availableMarkets = difference(
      mainRegionAndRiskTerritoryList
        ?.filter((mainRegionAndRiskTerritory) =>
          territories?.includes(mainRegionAndRiskTerritory.regionName)
        )
        ?.map(
          (mainRegionAndRiskTerritory) => mainRegionAndRiskTerritory.country
        ) || [],
      manuallyDeselectedMarkets
    );

    if (availableMarkets && !isEmpty(territories)) {
      if (
        isEqual(
          (initialData as { territories?: string[] })?.territories || [],
          territories
        )
      ) {
        form.setFieldsValue({ markets: validMarkets });
      } else {
        form.setFieldsValue({ markets: availableMarkets });
      }
    }
  }, [
    mainRegionAndRiskTerritoryList,
    markets,
    initialData,
    isLoadingMainRegionAndRiskTerritoryList,
  ]);

  return (
    <FormContainer
      title={t(
        mode === 'create' ? 'binder.setupNewBinder' : 'binder.updateBinder'
      )}
      loading={isLoading}
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={initialValues}
        onFinish={async (data) => {
          const sanitizedData = sanitizeFormValue(data);
          const fileIds = fileList
            .filter((file) => !!file.id)
            .map((file) => file.id) as string[];

          try {
            await onSubmit({ binder: sanitizedData, fileIds });
            message.success(
              t(
                `binder.binder${mode === 'create' ? 'Created' : 'Updated'}Successfully`
              )
            );
            resetAndNavigate();
          } catch (e) {
            handleError({
              error: e as { response: Response },
              defaultMessage: t(
                `binder.binder${mode === 'create' ? 'Created' : 'Updated'}Failed`
              ),
            });
          }
        }}
        className="space-y-8"
        onValuesChange={async () => {
          const values = form.getFieldsValue();
          const isValid = await validateForm(
            values,
            form,
            fields,
            getValidationSchema(t)
          );
          setIsFormValid(isValid);
        }}
      >
        {/* Binder Details Section */}
        <FormSection title={t('binder.form.binderDetail')}>
          <FormLayoutGroup mdColumns={2}>
            <Form.Item
              name="security"
              label={t('binder.financialCreditRating')}
            >
              <SortedSelect
                allowClear
                size="large"
                placeholder={t('general.selectField', {
                  field: t('binder.financialCreditRating'),
                })}
                options={Object.values(BinderResponseDtoSecurityEnum).map(
                  (security) => ({ label: security, value: security })
                )}
                suffixIcon={<ArrowDownIcon />}
              ></SortedSelect>
            </Form.Item>

            <Form.Item name="status" label={t('binder.status')}>
              <SortedSelect
                allowClear
                size="large"
                placeholder={t('general.selectField', {
                  field: t('binder.status'),
                })}
                options={Object.values(BinderResponseDtoStatusEnum).map(
                  (status) => ({
                    label: <StatusIndicator status={status} />,
                    value: status,
                  })
                )}
                suffixIcon={<ArrowDownIcon />}
              ></SortedSelect>
            </Form.Item>
          </FormLayoutGroup>

          <FormLayoutGroup mdColumns={2}>
            <Form.Item name="binderGroup" label={t('binder.binderGroup')}>
              <SortedSelect
                allowClear
                size="large"
                placeholder={t('general.selectField', {
                  field: t('binder.binderGroup'),
                })}
                options={Object.values(BinderResponseDtoBinderGroupEnum).map(
                  (group) => ({ label: group, value: group })
                )}
                suffixIcon={<ArrowDownIcon />}
              ></SortedSelect>
            </Form.Item>

            <Form.Item name="binderName" label={t('binder.binderName')}>
              <Input
                size="large"
                placeholder={t('general.enter', {
                  field: t('binder.binderName'),
                })}
              />
            </Form.Item>
          </FormLayoutGroup>

          <FormLayoutGroup mdColumns={1}>
            <Form.Item name="binderLongName" label={t('binder.binderLongName')}>
              <Input
                size="large"
                placeholder={t('general.enter', {
                  field: t('binder.binderLongName'),
                })}
              />
            </Form.Item>
          </FormLayoutGroup>

          <FormLayoutGroup mdColumns={3}>
            <Form.Item
              name="underwritingYear"
              label={t('submission.underwritingYear')}
            >
              <SortedSelect
                suffixIcon={<ArrowDownIcon />}
                allowClear
                size="large"
                placeholder={t('submission.selectUnderwritingYear')}
                options={getUnderwritingYearsOptions()}
                showSearch
              />
            </Form.Item>

            <Form.Item
              name="authorityStartDate"
              label={t('binder.authorityStartDate')}
            >
              <DatePicker
                allowClear
                format={'DD/MM/YYYY'}
                size="large"
                className="w-full"
                suffixIcon={<CalendarIcon />}
                onChange={(date) => {
                  // Clear end date if it's before the new start date
                  const endDate = form.getFieldValue('authorityEndDate');
                  if (endDate && date && endDate < date) {
                    form.setFieldValue('authorityEndDate', null);
                  }
                }}
              />
            </Form.Item>

            <Form.Item
              name="authorityEndDate"
              label={t('binder.authorityEndDate')}
            >
              <DatePicker
                allowClear
                format={'DD/MM/YYYY'}
                size="large"
                className="w-full"
                suffixIcon={<CalendarIcon />}
                disabledDate={(current) => {
                  const startDate = form.getFieldValue('authorityStartDate');
                  return startDate ? current < startDate : false;
                }}
              />
            </Form.Item>
          </FormLayoutGroup>
        </FormSection>

        {/* Max Limit Section */}
        <FormSection title={t('binder.maxLimit')}>
          <FormLayoutGroup>
            <Form.Item name="currency" label={t('general.currency')}>
              <SortedSelect
                suffixIcon={<ArrowDownIcon />}
                allowClear
                size="large"
                placeholder={t('submission.selectCurrency')}
                loading={isLoadingCurrencies}
                options={(currencies || []).map((currency) => ({
                  label: `${currency.name} (${currency.code})`,
                  value: currency.code,
                }))}
                showSearch
              ></SortedSelect>
            </Form.Item>

            <Form.Item
              name="primaryMaxLimit"
              label={t('binder.primaryMaxLimit')}
            >
              <InputNumber<number>
                size="large"
                className="w-full"
                min={0}
                precision={2}
                formatter={formatLocaleNumber}
                placeholder={t('general.enter', {
                  field: t('binder.primaryMaxLimit'),
                })}
              />
            </Form.Item>

            <Form.Item name="qsEolMaxLimit" label={t('binder.qsEolMaxLimit')}>
              <InputNumber<number>
                size="large"
                className="w-full"
                min={0}
                precision={2}
                formatter={formatLocaleNumber}
                placeholder={t('general.enter', {
                  field: t('binder.qsEolMaxLimit'),
                })}
              />
            </Form.Item>
          </FormLayoutGroup>
        </FormSection>

        {/* Territory Section */}
        <FormSection title={t('binder.territory')}>
          <FormLayoutGroup>
            <Form.Item name="territories" label={t('binder.territory')}>
              <SortedSelect
                allowClear
                size="large"
                placeholder={t('general.selectField', {
                  field: t('binder.territory'),
                })}
                showSearch
                mode="multiple"
                options={Object.values(BinderResponseDtoTerritoriesEnum).map(
                  (territory) => ({ label: territory, value: territory })
                )}
                suffixIcon={<ArrowDownIcon />}
              ></SortedSelect>
            </Form.Item>

            <Form.Item name="markets" label={t('binder.markets')}>
              <SortedSelect
                suffixIcon={<ArrowDownIcon />}
                allowClear
                size="large"
                placeholder={t('general.selectField', {
                  field: t('binder.markets'),
                })}
                showSearch
                onDeselect={(value) => {
                  setManuallyDeselectedMarkets((prev) => [...prev, value]);
                }}
                onSelect={(value) => {
                  setManuallyDeselectedMarkets((prev) =>
                    prev.filter((v) => v !== value)
                  );
                }}
                disabled={!territories || territories.length === 0}
                mode="multiple"
                loading={isLoadingMainRegionAndRiskTerritoryList}
                options={uniqBy(
                  (mainRegionAndRiskTerritoryList || []).map(
                    (mainRegionAndRiskTerritory) => ({
                      label: mainRegionAndRiskTerritory.country,
                      value: mainRegionAndRiskTerritory.country,
                    })
                  ),
                  'value'
                )}
              ></SortedSelect>
            </Form.Item>

            <Form.Item
              name="lineOfBusinesses"
              label={t('binder.lineOfBusinessProduct')}
            >
              <SortedSelect
                allowClear
                size="large"
                placeholder={t('general.selectField', {
                  field: t('binder.lineOfBusinesses'),
                })}
                showSearch
                mode="multiple"
                options={Object.values(
                  BinderResponseDtoLineOfBusinessesEnum
                ).map((lineOfBusiness) => ({
                  label: lineOfBusiness,
                  value: lineOfBusiness,
                }))}
                suffixIcon={<ArrowDownIcon />}
              ></SortedSelect>
            </Form.Item>

            <Form.Item
              name="excludedPrincipalOccupations"
              label={t('binder.excludedPrincipalOccupation')}
            >
              <SortedSelect
                allowClear
                size="large"
                placeholder={t('general.selectField', {
                  field: t('binder.excludedPrincipalOccupation'),
                })}
                loading={isLoadingExcludedPrincipalOccupations}
                showSearch
                mode="multiple"
                options={(excludedPrincipalOccupations || []).map(
                  (excludedPrincipalOccupation) => ({
                    label: excludedPrincipalOccupation.name,
                    value: excludedPrincipalOccupation.name,
                  })
                )}
                suffixIcon={<ArrowDownIcon />}
              ></SortedSelect>
            </Form.Item>
          </FormLayoutGroup>
        </FormSection>

        {/* Broker and Claims Section */}
        <FormSection title={t('binder.form.brokerAndClaims')}>
          <FormLayoutGroup mdColumns={1}>
            <Form.Item name="pinCommission" label={t('binder.pinCommission')}>
              <InputNumber
                size="large"
                className="w-full"
                min={0}
                max={100}
                precision={2}
                addonAfter="%"
                placeholder={t('general.enter', {
                  field: t('binder.pinCommission'),
                })}
              />
            </Form.Item>
          </FormLayoutGroup>

          <FormLayoutGroup mdColumns={2}>
            <Form.Item name="placeBroker" label={t('binder.placeBroker')}>
              <SortedSelect
                allowClear
                size="large"
                placeholder={t('general.selectField', {
                  field: t('binder.placeBroker'),
                })}
                options={Object.values(BinderResponseDtoPlaceBrokerEnum).map(
                  (placeBroker) => ({ label: placeBroker, value: placeBroker })
                )}
                suffixIcon={<ArrowDownIcon />}
              ></SortedSelect>
            </Form.Item>

            <Form.Item
              name="placementBrokerCommission"
              label={t('binder.placementBrokerCommission')}
            >
              <InputNumber<number>
                size="large"
                className="w-full"
                min={0}
                precision={2}
                formatter={formatLocaleNumber}
                placeholder={t('general.enter', {
                  field: t('binder.placementBrokerCommission'),
                })}
              />
            </Form.Item>
          </FormLayoutGroup>

          <FormLayoutGroup mdColumns={2}>
            <Form.Item
              name="defaultFloatBankAccount"
              label={t('binder.defaultFloatBankAccount')}
            >
              <Input
                size="large"
                placeholder={t('general.enter', {
                  field: t('binder.defaultFloatBankAccount'),
                })}
              />
            </Form.Item>

            <Form.Item
              name="claimsReferralAmount"
              label={t('binder.claimsReferralAmount')}
            >
              <InputNumber<number>
                size="large"
                className="w-full"
                min={0}
                precision={2}
                formatter={formatLocaleNumber}
                placeholder={t('general.enter', {
                  field: t('binder.claimsReferralAmount'),
                })}
              />
            </Form.Item>
          </FormLayoutGroup>

          <FormLayoutGroup mdColumns={1}>
            <Form.Item name="comment" label={t('general.comments')}>
              <TextArea
                rows={4}
                placeholder={t('general.enter', {
                  field: t('general.comments'),
                })}
              ></TextArea>
            </Form.Item>
          </FormLayoutGroup>
        </FormSection>

        {/* Attachments Section */}
        <FormSection title={t('binder.form.attachments')}>
          <FormLayoutGroup mdColumns={1}>
            <Form.Item className="mt-6">
              {fileSessionId && (
                <FileUploader
                  sessionId={fileSessionId}
                  entityType={CreateUploadedFilePayloadDtoEntityTypeEnum.Binder}
                  fileList={fileList}
                  setFileList={setFileList}
                />
              )}
            </Form.Item>
          </FormLayoutGroup>
        </FormSection>

        <FormActionButtons
          onCancel={() => {
            const currentValue = form.getFieldsValue();
            if (isEqual(currentValue, initialValues)) return resetAndNavigate();
            setShowConfirmation(true);
          }}
          isConfirmDisabled={
            !isFormValid ||
            isLoading ||
            fileList.some((file) => file.status === EFileStatus.UPLOADING)
          }
          confirmText={t(
            mode === 'create' ? 'binder.createBinder' : 'general.save'
          )}
        />

        <ConfirmationModal
          isOpen={showConfirmation}
          onClose={() => setShowConfirmation(false)}
          onConfirm={resetAndNavigate}
          title={t('forms.discardChangesTitle')}
          content={t('forms.discardChangesContent', {
            type: t('binder.binder'),
          })}
          okText={t('forms.discardChangesOkText')}
          cancelText={t('forms.discardChangesCancelText')}
          type={ConfirmationModalType.DANGER}
        />
      </Form>
    </FormContainer>
  );
};
